/**
 * Two-party preferred (TPP) head-to-head
 * The visualisation will include both won seats and predicted seats and have a line in the centre showing the target to winning the election.
 */

import React, { useCallback } from 'react'
import { getPartyColors, InterknowlogyExtendedParty } from '../data'
import { SeatBarGraph } from '../SeatCard/SeatCard'
import {
    SeatVoteCountContainer,
    SeatVotesContainer,
} from '../SeatCard/SeatCard.styled'
import {
    ElectionTppEstCardContentContainer,
    ElectionTppEstCardText,
    ElectionTppEstCardTitle,
    ElectionTppsEstCardErrorContainer,
    ElectionTppsEstCardErrorText,
} from './ElectionTppEstCard.styled'

interface ElectionTppEstData {
    leftPartyName: string
    leftPartyPercentage: number
    leftPartyColor: string
    rightPartyName: string
    rightPartyColor: string
}

interface ElectionTppEstCardProps {
    title: string
    data: InterknowlogyExtendedParty[]
}

export const ElectionTppEstCard = ({
    title,
    data,
}: ElectionTppEstCardProps) => {
    const getInitialData = useCallback(() => {
        // Grab the top 2 parties from the incoming data
        // These are sorted by seats won, so the top 2 are always what we want to show
        const partyData = data.splice(0, 2)

        // Calculate the percentage of the left party which is used to calculate the width of the bars
        const leftPartyPercentage = Math.round(
            (100 * partyData[0].votePct) /
                (partyData[0].votePct + partyData[1].votePct),
        )

        // Transform the data into the format required by the component
        const tppData: ElectionTppEstData = {
            leftPartyName:
                partyData[0].shortPartyName || partyData[0].partyName,
            leftPartyPercentage,
            leftPartyColor: getPartyColors(partyData[0].partyCode).primary,
            rightPartyName:
                partyData[1].shortPartyName || partyData[1].partyName,
            rightPartyColor: getPartyColors(partyData[1].partyCode).primary,
        }
        return tppData
    }, [data])

    const tppData = getInitialData()
    return (
        <ElectionTppEstCardContentContainer>
            {data.length < 2 ? (
                <>
                    <ElectionTppEstCardTitle>{title}</ElectionTppEstCardTitle>
                    <ElectionTppsEstCardErrorContainer>
                        <ElectionTppsEstCardErrorText bold>
                            Error
                        </ElectionTppsEstCardErrorText>
                        <ElectionTppsEstCardErrorText>
                            Refresh the page or try again later.
                        </ElectionTppsEstCardErrorText>
                    </ElectionTppsEstCardErrorContainer>
                </>
            ) : (
                <>
                    <ElectionTppEstCardTitle>{title}</ElectionTppEstCardTitle>
                    <div>
                        <SeatVotesContainer>
                            <SeatVoteCountContainer
                                color={tppData.leftPartyColor}
                            >
                                <ElectionTppEstCardText variant="percentage">
                                    {tppData.leftPartyPercentage}%
                                </ElectionTppEstCardText>
                                <ElectionTppEstCardText variant="label">
                                    {tppData.leftPartyName}
                                </ElectionTppEstCardText>
                            </SeatVoteCountContainer>
                            <SeatVoteCountContainer
                                color={tppData.rightPartyColor}
                            >
                                <ElectionTppEstCardText variant="label">
                                    {tppData.rightPartyName}
                                </ElectionTppEstCardText>
                                <ElectionTppEstCardText variant="percentage">
                                    {100 - tppData.leftPartyPercentage}%
                                </ElectionTppEstCardText>
                            </SeatVoteCountContainer>
                        </SeatVotesContainer>
                        <SeatBarGraph
                            candidateOneColor={tppData.leftPartyColor}
                            candidateTwoColor={tppData.rightPartyColor}
                            candidateOnePct={tppData.leftPartyPercentage}
                        ></SeatBarGraph>
                    </div>
                </>
            )}
        </ElectionTppEstCardContentContainer>
    )
}
